import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Components, Feedback, Utils, translate,
} from 'versafleet-core';
import * as Sentry from '@sentry/browser';
import ReduxActions from 'reduxActions';
import withRouterHooks from 'utils/routerHelper';
import classNames from 'classnames';

const { PusherHelper } = Utils;

const formatOptions = [{
  value: 'xlsx',
  text: 'xlsx',
}, {
  value: 'csv',
  text: 'csv',
}];

const FAILED = 'failed';
const COMPLETED = 'completed';
const TASK_COUNT_LIMIT = 5000;

class JobExportForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      form: this._getDefaultForm(),
      error: [],
      isChanged: false,
      isExporting: false,
    };
    this.basePath = 'jobs';
  }

  UNSAFE_componentWillMount() {
    this.props.dispatch(ReduxActions.task.fetchExportTags());
    this.props.dispatch(ReduxActions.task.resetExportTaskCount());
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!Utils.Helper.isNullOrUndefined(nextProps.exportTags.base)
      && nextProps.exportTags.base !== this.props.exportTags.base) {
      const allExportTags = this._combineExportTags(nextProps.exportTags);
      const tags = {};
      const localStorageTags = JSON.parse(window.localStorage.getItem('job_export_tags'));
      allExportTags.forEach((exportTag) => {
        // All task/job/item information are selected for export as default,
        // except for custom field information
        const isCustomFieldTag = exportTag.includes('#');
        const defaultSelection = !isCustomFieldTag;

        tags[exportTag] = Utils.Helper.isNullOrUndefined(localStorageTags) ? defaultSelection
          : localStorageTags.indexOf(exportTag) !== -1;
      });

      this.setState(prevState => ({
        form: {
          ...prevState.form,
          tags,
        },
      }));
    }
  }

  _getDefaultForm = () => ({
    format: 'xlsx',
    from_date: null,
    to_date: null,
    archived: false,
    job_archived: false,
    tags: {},
  });

  _getAttributes(f) {
    const form = f || this.state.form;
    const { exportTags } = this.props;

    const allExportTags = this._combineExportTags(exportTags);

    const tags = [];
    for (let i = 0; i < allExportTags.length; i += 1) {
      if (form.tags[allExportTags[i]] === true) {
        tags.push(allExportTags[i]);
      }
    }

    // Store tags to local storage
    window.localStorage.setItem('job_export_tags', JSON.stringify(tags));

    return {
      task: {
        ...form,
        state: form.state,
        job_state: form.job_state,
        tags,
      },
    };
  }

  _getFormError() {
    const messages = this.state.error;
    return {
      from_date: messages.from_date,
      to_date: messages.to_date,
    };
  }

  _getJobStatusOptions = () => ([{
    value: this.props.strings.Job.status.in_progress,
    text: 'In Progress',
  }, {
    value: this.props.strings.Job.status.completed,
    text: 'Completed',
  }, {
    value: this.props.strings.Job.status.draft,
    text: 'Draft',
  }, {
    value: this.props.strings.Job.status.submitted,
    text: 'Submitted',
  }, {
    value: this.props.strings.Job.status.rejected,
    text: 'Rejected',
  }]);

  _getTaskStatusOptions = () => ([{
    text: this.props.strings.Task.status.unassigned,
    value: 'unassigned',
  }, {
    text: this.props.strings.Task.status.assigned,
    value: 'assigned',
  }, {
    text: this.props.strings.Task.status.waiting_for_acknowledgement,
    value: 'waiting_for_acknowledgement',
  }, {
    text: this.props.strings.Task.status.acknowledged,
    value: 'acknowledged',
  }, {
    text: this.props.strings.Task.status.started,
    value: 'started',
  }, {
    text: this.props.strings.Task.status.successful,
    value: 'successful',
  }, {
    text: this.props.strings.Task.status.failed,
    value: 'failed',
  }, {
    text: this.props.strings.Task.status.declined,
    value: 'declined',
  }, {
    text: this.props.strings.Task.status.cancelled,
    value: 'cancelled',
  }, {
    text: this.props.strings.Task.status.collected,
    value: 'collected',
  }]);

  _onFormChange(fieldName, value) {
    let { form } = this.state;
    if (fieldName === 'archived' || fieldName === 'job_archived') {
      const modifiedValue = (value === true) ? undefined : false;
      form = Utils.Helper.setFilter(this.state.form, fieldName, modifiedValue);
    } else {
      form = Utils.Helper.setFilter(this.state.form, fieldName, value);
    }

    if (!Utils.Helper.isNullOrUndefined(form.from_date)
      && !Utils.Helper.isNullOrUndefined(form.to_date) && fieldName.indexOf('tags') === -1 && fieldName !== 'format') {
      Utils.FullScreenLoading.FullScreenLoadingManager.open(
        this.props.strings.ExportForm.checkingCount,
      );
      this.props.dispatch(ReduxActions.task.checkExportTaskCount(this._getAttributes(form)))
        .catch(error => this.setState({ error }))
        .then(() => {
          Utils.FullScreenLoading.FullScreenLoadingManager.close();
        });
    }

    this.setState({ form, isChanged: true });
  }

  async _export() {
    const { strings } = this.props;
    let query = this._getAttributes();
    const pusherId = `task_export_${PusherHelper.getNewPusherId()}`;
    query = { ...query.task, pusher_id: pusherId };
    const exportTasks = async () => {
      PusherHelper.bindChannel('user', pusherId,
        result => this._handleExportResult(result, pusherId));
      await ReduxActions.task.exportTasks(query);
    };
    Feedback.Progress.Manager.updateProgress(pusherId, { onRetry: exportTasks });
    Utils.FullScreenLoading.FullScreenLoadingManager
      .open(strings.ExportForm.loading);
    setTimeout(() => Utils.FullScreenLoading.FullScreenLoadingManager.close(), 500);
    try {
      await exportTasks();
    } catch (error) {
      this.setState({ error });
    }
  }

  _handleExportResult = (result, pusherId) => {
    Feedback.Progress.Manager.updateProgress(pusherId, result);
    if (result.status === FAILED) {
      PusherHelper.unbindChannel('user', pusherId);
      Sentry.captureException(result.error);
    }
    if (result.status === COMPLETED) {
      const link = document.createElement('a');
      link.href = result.url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      PusherHelper.unbindChannel('user', pusherId);
      this.setState({ isChanged: false });
    }
  }

  _isTaskCountExceedsLimit() {
    const { taskCount } = this.props;
    return !Utils.Helper.isNullOrUndefined(taskCount) && taskCount > TASK_COUNT_LIMIT;
  }

  _combineExportTags = (exportTags) => {
    const allExportTags = [];
    const base = exportTags.base || {};
    const fieldOrder = ['job', 'job_custom_notes', 'task', 'task_custom_notes', 'item', 'custom_field_groups'];
    fieldOrder.forEach((field) => {
      const tags = [
        'custom_field_groups',
        'task_custom_notes',
        'job_custom_notes',
      ].includes(field)
        ? (exportTags[field] || [])
        : (base[field] || []);
      tags.forEach((exportTagGroup) => {
        allExportTags.push(exportTagGroup.tag);
      });
    });

    return allExportTags;
  }

  _renderSelectableDetailsForm() {
    const { exportTags } = this.props;
    const fieldOrder = ['job', 'job_custom_notes', 'task', 'task_custom_notes', 'item', 'custom_field_groups'];
    const base = exportTags.base || {};
    return (
      <div>
        {
          fieldOrder.map((field) => {
            const tags = [
              'custom_field_groups',
              'task_custom_notes',
              'job_custom_notes',
            ].includes(field)
              ? (exportTags[field] || [])
              : (base[field] || []);

            if (tags.length === 0) {
              return false;
            }
            return (
              <div>
                <Components.Form.Block level={1}>
                  {Utils.TranslationHelper.replaceTag(
                    this.props.strings.ExportForm.allFieldDetail, {
                      field: Utils.Formatter.capitalizeFirstLetter(
                        this.props.strings.ExportForm[field],
                      ),
                    },
                  )}
                </Components.Form.Block>
                <Components.Form.Block level={2}>
                  {tags.map(group => (
                    <div>
                      <Components.Input.Checkbox
                        id="vf-job__export-form__selectable-details-checkbox"
                        onChange={val => this._onFormChange(`tags.${group.tag}`, val)}
                        value={this.state.form.tags[group.tag]}
                      >
                        {group.title}
                      </Components.Input.Checkbox>
                    </div>
                  ))}
                </Components.Form.Block>
                <Components.Form.Divider />
              </div>
            );
          })
        }
      </div>
    );
  }

  _renderRightPanel() {
    const { taskCount } = this.props;

    const taskCountClassNames = classNames({
      'vf-job__export-form__task-count': true,
      'vf-job__export-form__task-count--error': this._isTaskCountExceedsLimit(),
    });

    return (
      <Components.Form.RightPanel>
        <Components.Form.MainButton
          disabled={this.state.isExporting || Utils.Helper.isNullOrUndefined(this.props.taskCount)
            || this._isTaskCountExceedsLimit()}
          id="vf-job__export-form__export-button"
          onClick={() => this._export()}
        >
          {this.props.strings.ExportForm.title}
        </Components.Form.MainButton>
        <div className={taskCountClassNames}>
          {Utils.Helper.isNullOrUndefined(this.props.taskCount) ? '' : `${taskCount} Tasks`}
        </div>
      </Components.Form.RightPanel>
    );
  }

  _renderMenu() {
    return (
      <Components.Form.Title>
        <Components.Form.Title.Item isHighlighted>
          {this.props.strings.ExportForm.title}
        </Components.Form.Title.Item>
      </Components.Form.Title>
    );
  }

  render() {
    const error = this._getFormError();
    const taskCountWarning = Utils.TranslationHelper.replaceTag(
      this.props.strings.ExportForm.taskCountWarningText, { taskCountLimit: TASK_COUNT_LIMIT },
    );
    return (
      <Components.Form.CoreLayout
        isChanged={this.state.isChanged}
        onClose={() => (this.props.location.pathname.includes('submissions')
          ? this.props.navigate('/submissions')
          : this.props.navigate('/in_progress'))}
        rightPanel={this._renderRightPanel()}
        title={this._renderMenu()}
      >
        <Components.Form.MainHeader
          iconClassName="versa-export"
          iconText=""
          subtitle={(
            <div>
              <div>
                {this.props.strings.ExportForm.title}
              </div>
              {this._isTaskCountExceedsLimit() ? (
                <div className="vf-job__export-form__error-message">
                  {Utils.TranslationHelper.replaceTag(this.props.strings.ExportForm.title, {
                    taskCountWarning: <b>{taskCountWarning}</b>,
                  })}
                </div>
              ) : false}
            </div>
          )}
          title="Export Jobs"
        />
        <Components.Form.Divider size="LARGE" />
        <Components.Form.Block level={1}>
          {this.props.strings.ExportForm.dateRange}
        </Components.Form.Block>
        <Components.Form.Block contentClassName="vf-job__export-form__date" level={2}>
          <Components.Input.DatePicker
            className="vf-job__export-form__date__picker"
            error={error.from_date}
            id="vf-job__export-form__datepicker--left"
            onChange={val => this._onFormChange('from_date', val)}
            placeholder={this.props.strings.ExportForm.startDate}
            value={this.state.form.from_date}
          />
          <span className="vf-job__export-form__date__picker__separator">-</span>
          <Components.Input.DatePicker
            className="vf-job__export-form__date__picker"
            error={error.to_date}
            id="vf-job__export-form__datepicker--right"
            onChange={val => this._onFormChange('to_date', val)}
            placeholder={this.props.strings.ExportForm.endDate}
            value={this.state.form.to_date}
          />
        </Components.Form.Block>
        <Components.Form.Divider size="LARGE" />
        {!Utils.Helper.isNullOrUndefined(this.state.form.from_date)
          && !Utils.Helper.isNullOrUndefined(this.state.form.to_date) ? (
            <div>
              <Components.Form.Block level={1}>
                {this.props.strings.ExportForm.status}
              </Components.Form.Block>
              <Components.Form.Block level={2}>
                <Components.Input.Select
                  emptyOptionText={this.props.strings.ExportForm.allJobStatus}
                  id="vf-job__export-form__job-status"
                  onChange={val => this._onFormChange('job_state', val)}
                  options={this._getJobStatusOptions()}
                  placeholder="All Job Status"
                  value={this.state.form.job_state}
                />
                <Components.Input.Select
                  emptyOptionText={this.props.strings.ExportForm.allTaskStatus}
                  id="vf-job__export-form__task-status"
                  onChange={val => this._onFormChange('state', val)}
                  options={this._getTaskStatusOptions()}
                  placeholder="All Task Status"
                  value={this.state.form.state}
                />
              </Components.Form.Block>
              <Components.Form.Divider />

              <Components.Form.Block level={1}>
                {this.props.strings.ExportForm.format}
              </Components.Form.Block>
              <Components.Form.Block level={2}>
                <Components.Input.Select
                  hasEmptyOption={false}
                  id="vf-job__export-form__format"
                  onChange={val => this._onFormChange('format', val)}
                  options={formatOptions}
                  value={this.state.form.format}
                />
              </Components.Form.Block>
              <Components.Form.Divider />

              {this._renderSelectableDetailsForm()}

              <Components.Form.Block level={1}>
                <Components.Input.Checkbox
                  id="vf-job__export-form__toggle-archived-task"
                  onChange={val => this._onFormChange('archived', val)}
                  value={typeof this.state.form.archived === 'undefined'}
                >
                  {this.props.strings.ExportForm.includeArchivedTask}
                </Components.Input.Checkbox>
              </Components.Form.Block>

              <Components.Form.Block level={1}>
                <Components.Input.Checkbox
                  id="vf-job__export-form__toggle-archived-job"
                  onChange={val => this._onFormChange('job_archived', val)}
                  value={typeof this.state.form.job_archived === 'undefined'}
                >
                  {this.props.strings.ExportForm.includeArchivedJob}
                </Components.Input.Checkbox>
              </Components.Form.Block>
            </div>
          ) : false}

        {/* So that there's space for datepicker to expand */}
        <Components.Form.Divider size="LARGE" />
        <Components.Form.Divider size="LARGE" />
        <Components.Form.Divider size="LARGE" />
        <Components.Form.Divider size="LARGE" />
        <Components.Form.Divider size="LARGE" />
        <Components.Form.Divider size="LARGE" />
      </Components.Form.CoreLayout>
    );
  }
}

JobExportForm.displayName = 'Job Export Form';

JobExportForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  exportTags: PropTypes.shape({
    base: PropTypes.shape(),
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  navigate: PropTypes.func.isRequired,
  strings: PropTypes.shape({
    ExportForm: PropTypes.shape({
      checkingCount: PropTypes.string,
      loading: PropTypes.string,
      allFieldDetail: PropTypes.string,
      title: PropTypes.string,
      taskCountWarningText: PropTypes.string,
      dateRange: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      status: PropTypes.string,
      allJobStatus: PropTypes.string,
      allTaskStatus: PropTypes.string,
      format: PropTypes.string,
      includeArchivedTask: PropTypes.string,
      includeArchivedJob: PropTypes.string,
    }),
    Task: PropTypes.shape({
      status: PropTypes.shape({
        unassigned: PropTypes.string,
        assigned: PropTypes.string,
        waiting_for_acknowledgement: PropTypes.string,
        acknowledged: PropTypes.string,
        started: PropTypes.string,
        successful: PropTypes.string,
        failed: PropTypes.string,
        declined: PropTypes.string,
        cancelled: PropTypes.string,
        collected: PropTypes.string,
      }),
    }),
    Job: PropTypes.shape({
      status: PropTypes.shape({
        in_progress: PropTypes.string,
        completed: PropTypes.string,
        draft: PropTypes.string,
        submitted: PropTypes.string,
        rejected: PropTypes.string,
      }),
    }),
  }).isRequired,
  taskCount: PropTypes.number,
};

JobExportForm.defaultProps = {
  taskCount: null,
};

function mapStateToProps(state) {
  const { driver, task } = state;
  return {
    exportTags: task.exportTags,
    isCheckingExportCount: task.isCheckingExportCount,
    optionDrivers: (Utils.Helper.isNullOrUndefined(driver)) ? [] : driver.optionDrivers,
    taskCount: task.taskCount,
  };
}

export default withRouterHooks(connect(mapStateToProps)(translate('')(JobExportForm)));
